/**
 * Shared search filter parsing utilities
 * Used by /api/search/listings, /api/search/showcase, and /api/recommendations/*
 */

import { parseTagFacets } from './tag-facet-param';
import { BROWSABLE_STATUSES, BROWSABLE_WITH_SOLD_STATUSES } from '../data/js/listing-statuses';
import { parseShuffleSeed } from './seeded-shuffle';

export interface SearchQueryParams {
  // Text search
  q?: string;
  // Tag filter: comma-separated string OR repeated `tags=` params (preferred for
  // tag values that contain commas)
  tags?: string | string[];
  // Category filters
  categoryId?: string;
  categoryGroupId?: string;
  excludeCategoryId?: string;
  excludeCategoryGroupId?: string;
  // Listing/user filters
  userId?: string;
  excludeListingId?: string;
  excludeListingIds?: string;
  excludeUserId?: string;
  dataSourceId?: string;
  // Price
  minPrice?: string;
  maxPrice?: string;
  // Status
  sold?: string;
  status?: string;
  // Attributes
  itemCondition?: string;
  shippable?: string;
  membershipType?: string;
  featured?: string;
  deals?: string;
  quality?: string;
  hasPhoto?: string;
  // Filterable listing attributes (utils/listing-attributes.ts): each a
  // comma-separated list or repeated params, matching any of them.
  gender?: string | string[];
  ageGroup?: string | string[];
  size?: string | string[];
  color?: string | string[];
  brand?: string | string[];
  material?: string | string[];
  /** Tag and type filters, `species:dogs|cats;type:206` (utils/tag-facet-param.ts). */
  tf?: string | string[];
  // Geo
  latitude?: string;
  longitude?: string;
  distance?: string;
  distanceStart?: string;
  // Pagination
  page?: string;
  limit?: string;
  offset?: string;
  // The page load's shuffle seed (utils/seeded-shuffle.ts)
  seed?: string;
  // Sorting
  sortBy?: string;
}

export interface ParsedSearchOptions {
  q?: string;
  /** Typed words (or one seller's own shelf, set by the route): keep listings no row ever shows (utils/not-recommended.ts). */
  includeNotRecommended?: boolean;
  tags?: string[];
  categoryId?: number | number[];
  categoryGroupId?: number;
  excludeCategoryId?: number | number[];
  excludeCategoryGroupId?: number | number[];
  userId?: number;
  excludeListingId?: number;
  excludeListingIds?: number[];
  excludeUserId?: number;
  dataSourceId?: number;
  minPrice?: number;
  maxPrice?: number;
  status?: number | number[];
  itemCondition?: number[];
  shippable?: boolean;
  membershipType?: number | number[];
  featured?: boolean;
  deals?: boolean;
  quality?: boolean;
  hasPhoto?: boolean;
  latitude?: number;
  longitude?: number;
  distance?: number;
  distanceStart?: number;
  page?: number;
  limit?: number;
  offset?: number;
  shuffleSeed?: number;
  sortBy?: string;
  gender?: string[];
  ageGroup?: string[];
  size?: string[];
  color?: string[];
  brand?: string[];
  material?: string[];
  tagFacets?: Record<string, string[]>;
}

/** Values each closed attribute filter accepts; anything else is dropped, never passed to Elasticsearch. */
export const ATTRIBUTE_FILTER_VALUES = {
  gender: ['female', 'male', 'unisex'],
  ageGroup: ['adult', 'kids', 'baby'],
  color: ['black', 'white', 'gray', 'brown', 'beige', 'red', 'pink', 'orange', 'yellow', 'green', 'blue', 'purple', 'gold', 'silver', 'multicolor', 'clear'],
} as const;

/** Most values one attribute filter takes, and the longest one. */
const MAX_FILTER_VALUES = 30;
const MAX_FILTER_VALUE_LENGTH = 100;

/**
 * A list param: comma-separated, or repeated (`brand=Gucci&brand=Prada`) for
 * values that contain commas. Trimmed, de-duplicated, bounded; restricted to
 * `allowed` when given.
 */
export function parseStringList(value: string | string[] | undefined, allowed?: readonly string[]): string[] | undefined {
  if (value === undefined) return undefined;
  // Next parses a repeated param as string[], so every entry is a string.
  const raw = Array.isArray(value) ? value : value.split(',');
  const list = [...new Set(raw.map(v => v.trim()).filter(v => v.length > 0 && v.length <= MAX_FILTER_VALUE_LENGTH))]
    .filter(v => !allowed || allowed.includes(v))
    .slice(0, MAX_FILTER_VALUES);
  return list.length > 0 ? list : undefined;
}

/** Page size when the caller does not ask for one. */
export const DEFAULT_SEARCH_LIMIT = 32;

/**
 * Largest page any caller may ask for. The biggest legitimate request is the
 * seller's own listings page at 200 per page; anything above that is either a
 * mistake or someone probing for an expensive query.
 */
export const MAX_SEARCH_LIMIT = 200;

/** Elasticsearch refuses `from + size` past 10,000 rows. */
export const MAX_SEARCH_OFFSET = 10_000 - MAX_SEARCH_LIMIT;

/**
 * Parse status from sold/status params
 * sold=1 includes sold items (status 5), otherwise excludes them
 */
export function parseStatusFilter(query: { sold?: string; status?: string }): number[] {
  if (query.sold !== undefined) {
    const includeSold = query.sold === 'true' || query.sold === '1';
    return includeSold ? [...BROWSABLE_WITH_SOLD_STATUSES] : [...BROWSABLE_STATUSES];
  }
  if (query.status !== undefined) {
    const statusString = query.status;
    if (statusString.includes(',')) {
      return statusString.split(',').map(s => parseInt(s, 10)).filter(s => !isNaN(s));
    }
    const single = parseInt(statusString, 10);
    // A malformed status falls back to the browse default rather than to a
    // wider set — an unparseable param must never widen what a shopper sees.
    return isNaN(single) ? [...BROWSABLE_STATUSES] : [single];
  }
  return [...BROWSABLE_STATUSES]; // Default: exclude sold (5) and held (4)
}

/**
 * Parse comma-separated integers into array
 */
export function parseIntArray(value?: string): number[] | undefined {
  if (!value) return undefined;
  const arr = value.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
  return arr.length > 0 ? arr : undefined;
}

/**
 * Parse single or comma-separated integers
 * Returns single number if one value, array if multiple
 */
export function parseIntOrArray(value?: string): number | number[] | undefined {
  if (!value) return undefined;
  if (value.includes(',')) {
    return parseIntArray(value);
  }
  const single = parseInt(value, 10);
  return isNaN(single) ? undefined : single;
}

/**
 * Parse boolean from string ('true', '1' = true)
 */
export function parseBool(value?: string): boolean | undefined {
  if (value === undefined) return undefined;
  return value === 'true' || value === '1';
}

/**
 * Parse float from string
 */
export function parseFloatParam(value?: string): number | undefined {
  if (!value) return undefined;
  const num = parseFloat(value);
  return isNaN(num) ? undefined : num;
}

/**
 * Parse int from string
 */
export function parseIntParam(value?: string): number | undefined {
  if (!value) return undefined;
  const num = parseInt(value, 10);
  return isNaN(num) ? undefined : num;
}

/**
 * Main parser function - converts query string params to typed search options
 */
export function parseSearchQueryParams(query: SearchQueryParams): ParsedSearchOptions {
  const options: ParsedSearchOptions = {};

  // Text search
  if (query.q && typeof query.q === 'string' && query.q.trim().length > 0) {
    options.q = query.q.trim();
  }

  // Tag filter — accept either a comma-separated string or repeated `tags=`
  // params (Next.js parses repeated params as string[]). Repeated params are
  // the only safe way to pass tag values that contain commas.
  if (query.tags) {
    const raw = Array.isArray(query.tags) ? query.tags : query.tags.split(',');
    const tagArray = raw.map(t => t.trim()).filter(Boolean);
    if (tagArray.length > 0) {
      options.tags = tagArray;
    }
  }

  // Category filters
  options.categoryId = parseIntOrArray(query.categoryId);
  // Positive only. The For You tab marks itself with group -1 in the category
  // bar, and that leaked into search URLs, where a term filter on -1 matched
  // nothing: `pottery` answered 595 listings, `pottery&categoryGroupId=-1` 0.
  const group = parseIntParam(query.categoryGroupId);
  options.categoryGroupId = group !== undefined && group > 0 ? group : undefined;
  options.excludeCategoryId = parseIntOrArray(query.excludeCategoryId);
  options.excludeCategoryGroupId = parseIntOrArray(query.excludeCategoryGroupId);

  // Listing/user filters
  options.excludeListingId = parseIntParam(query.excludeListingId);
  // Set of already-shown listing IDs to exclude (fallback/pagination dedup).
  // Capped defensively so the terms filter stays well under index.max_terms_count.
  options.excludeListingIds = parseIntArray(query.excludeListingIds)?.slice(0, 2000);
  options.excludeUserId = parseIntParam(query.excludeUserId);
  options.dataSourceId = parseIntParam(query.dataSourceId);

  // Price
  options.minPrice = parseFloatParam(query.minPrice);
  options.maxPrice = parseFloatParam(query.maxPrice);

  // Status (sold takes precedence)
  options.status = parseStatusFilter(query);

  // Item condition
  options.itemCondition = parseIntArray(query.itemCondition);

  // Boolean filters
  options.shippable = parseBool(query.shippable);
  options.featured = parseBool(query.featured);
  options.deals = parseBool(query.deals);
  options.quality = parseBool(query.quality);
  if (query.hasPhoto !== undefined) {
    options.hasPhoto = parseBool(query.hasPhoto);
  }

  // Membership type
  options.membershipType = parseIntOrArray(query.membershipType);

  // Geo
  options.latitude = parseFloatParam(query.latitude);
  options.longitude = parseFloatParam(query.longitude);
  options.distance = parseFloatParam(query.distance);
  options.distanceStart = parseFloatParam(query.distanceStart);

  // Pagination. `limit` used to be whatever the caller wrote, on a public
  // unauthenticated endpoint — `?limit=100000` was one request away from asking
  // Elasticsearch to build a hundred-thousand-hit page. Clamped to a size a real
  // client asks for; the default is unchanged.
  const requestedPage = parseIntParam(query.page);
  options.page = requestedPage && requestedPage > 0 ? requestedPage : 1;
  const requestedLimit = parseIntParam(query.limit);
  options.limit = requestedLimit && requestedLimit > 0
    ? Math.min(requestedLimit, MAX_SEARCH_LIMIT)
    : DEFAULT_SEARCH_LIMIT;
  // A start row, for a list whose first page was not this endpoint's page 1
  // (a showcase row's first items come from the showcase route, 7 of them, so
  // page 2 at 10 per page skipped items 8-10). Overrides `page`.
  const requestedOffset = parseIntParam(query.offset);
  if (requestedOffset !== undefined && requestedOffset >= 0) {
    options.offset = Math.min(requestedOffset, MAX_SEARCH_OFFSET);
  }

  const shuffleSeed = parseShuffleSeed(query.seed);
  if (shuffleSeed !== undefined) options.shuffleSeed = shuffleSeed;

  // Sorting
  if (query.sortBy && typeof query.sortBy === 'string') {
    options.sortBy = query.sortBy;
  }

  // Listing attributes
  options.gender = parseStringList(query.gender, ATTRIBUTE_FILTER_VALUES.gender);
  options.ageGroup = parseStringList(query.ageGroup, ATTRIBUTE_FILTER_VALUES.ageGroup);
  options.color = parseStringList(query.color, ATTRIBUTE_FILTER_VALUES.color);
  options.size = parseStringList(query.size);
  options.brand = parseStringList(query.brand);
  options.material = parseStringList(query.material);
  options.tagFacets = parseTagFacets(query.tf);

  // Someone who typed words is asking for what they typed. Everything else
  // through here is browse, which leaves those listings out.
  if (options.q) options.includeNotRecommended = true;

  return options;
}
